import type { PropsWithChildren } from 'react';

import { cn } from 'cn';
import { useTranslation } from 'react-i18next';

import { Spinner } from '@vestigas/ui';

type DataCardProps = PropsWithChildren<{
  hasDivider?: boolean;
}> &
  ComponentStyling;

type DataCardSubComponentProps = PropsWithChildren & ComponentStyling;

type DataCardBodyProps = DataCardSubComponentProps & {
  readonly isLoading?: boolean;
};

/**
 * DataCardRoot
 *
 * A compound component that renders a data card with a header, body, and footer.
 */
function DataCardRoot({
  children,
  className,
  hasDivider,
  style,
}: DataCardProps) {
  return (
    <div
      className={cn(
        'grid gap-y-0 divide-tiga-border-lighter rounded-lg border border-tiga-border-lighter bg-white contain-paint',
        {
          'divide-y': hasDivider,
        },
        className,
      )}
      style={style}
    >
      {children}
    </div>
  );
}

function DataCardHeader({
  children,
  className,
  style,
}: DataCardSubComponentProps) {
  return (
    <div className={cn('row-1 p-4', className)} style={style}>
      {children}
    </div>
  );
}

function DataCardBody({
  children,
  className,
  isLoading,
  style,
}: DataCardBodyProps) {
  const { t } = useTranslation('common');

  return (
    <div className={cn('relative row-2 min-w-0 p-4', className)} style={style}>
      {children}
      {isLoading && (
        <div className="animate-loadingOverlay absolute -inset-1 z-10 rounded-md">
          <Spinner initialDelay={1600} title={t(($) => $.loadingData)} />
        </div>
      )}
    </div>
  );
}

function DataCardFooter({
  children,
  className,
  style,
}: DataCardSubComponentProps) {
  return (
    <div className={cn('row-3 px-4 py-2', className)} style={style}>
      {children}
    </div>
  );
}

// Compound component with sub-components attached
export const DataCard = Object.assign(DataCardRoot, {
  Header: DataCardHeader,
  Body: DataCardBody,
  Footer: DataCardFooter,
});

export { DataCardBody, DataCardFooter, DataCardHeader };
